import { z } from 'zod';
import { Channel, ConsentBasis, LeadStage } from '../enums';

export const LeadSource = {
  META_LEAD_FORM: 'META_LEAD_FORM',
  META_WHATSAPP: 'META_WHATSAPP',
  GOOGLE_FORM: 'GOOGLE_FORM',
  GOOGLE_CALL: 'GOOGLE_CALL',
  SITE_FORM: 'SITE_FORM',
  WHATSAPP: 'WHATSAPP',
  PHONE: 'PHONE',
  CRM: 'CRM',
  MANUAL: 'MANUAL',
  OTHER: 'OTHER',
} as const;
export type LeadSource = (typeof LeadSource)[keyof typeof LeadSource];

export const CrmProvider = {
  IXC: 'IXC',
  SGP: 'SGP',
  HUBSOFT: 'HUBSOFT',
  GENERIC_WEBHOOK: 'GENERIC_WEBHOOK',
} as const;
export type CrmProvider = (typeof CrmProvider)[keyof typeof CrmProvider];

/**
 * Dado pessoal só trafega para o registro do lead; para CAPI/offline conversions
 * é sempre normalizado e hasheado (SHA-256) antes do envio.
 */
export const leadSchema = z.object({
  clientId: z.string().uuid(),
  externalId: z.string().max(120).optional(),
  source: z.nativeEnum(LeadSource),
  channel: z.nativeEnum(Channel).optional(),
  campaignExternalId: z.string().max(120).optional(),
  adExternalId: z.string().max(120).optional(),
  name: z.string().max(200).optional(),
  email: z.string().email().optional(),
  phone: z.string().max(30).optional(),
  city: z.string().max(120).optional(),
  state: z.string().length(2).optional(),
  postalCode: z.string().max(12).optional(),
  planInterest: z.string().max(160).optional(),
  stage: z.nativeEnum(LeadStage).default('LEAD'),
  occurredAt: z.coerce.date(),
  /** Identificadores de clique para atribuição. */
  fbclid: z.string().max(500).optional(),
  gclid: z.string().max(500).optional(),
  utm: z
    .object({
      source: z.string().max(120).optional(),
      medium: z.string().max(120).optional(),
      campaign: z.string().max(160).optional(),
      content: z.string().max(160).optional(),
      term: z.string().max(160).optional(),
    })
    .optional(),
  // LGPD
  consentBasis: z.nativeEnum(ConsentBasis).default('CONSENT'),
  consentGivenAt: z.coerce.date().optional(),
  consentText: z.string().max(2000).optional(),
  consentSourceUrl: z.string().max(2048).optional(),
  allowAdsSharing: z.boolean().default(false),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type LeadInput = z.infer<typeof leadSchema>;

export const updateLeadStageSchema = z.object({
  stage: z.nativeEnum(LeadStage),
  occurredAt: z.coerce.date().default(() => new Date()),
  revenueCents: z.number().int().min(0).optional(),
  lostReason: z.string().max(500).optional(),
  note: z.string().max(2000).optional(),
});
export type UpdateLeadStageInput = z.infer<typeof updateLeadStageSchema>;

export const funnelQuerySchema = z.object({
  clientId: z.string().uuid(),
  from: z.coerce.date(),
  to: z.coerce.date(),
  groupBy: z.enum(['CAMPAIGN', 'CHANNEL', 'CITY', 'PLAN', 'SOURCE']).default('CAMPAIGN'),
});
export type FunnelQuery = z.infer<typeof funnelQuerySchema>;

export interface FunnelRow {
  key: string;
  label: string;
  spendCents: number;
  leads: number;
  attended: number;
  qualified: number;
  withCoverage: number;
  contracts: number;
  installs: number;
  revenueCents: number;
  cplCents: number;
  costPerQualifiedCents: number;
  costPerInstallCents: number;
  roas: number;
}

export const CONVERSION_EVENT_NAMES = {
  LEAD: 'Lead',
  CONTACT: 'Contact',
  SCHEDULE: 'Schedule',
  SUBSCRIBE: 'Subscribe',
  PURCHASE: 'Purchase',
  COMPLETE_REGISTRATION: 'CompleteRegistration',
} as const;

/** Mapeamento de estágio do funil → evento enviado às plataformas. */
export const STAGE_TO_EVENT: Partial<Record<LeadStage, string>> = {
  LEAD: CONVERSION_EVENT_NAMES.LEAD,
  QUALIFIED: CONVERSION_EVENT_NAMES.COMPLETE_REGISTRATION,
  CONTRACT: CONVERSION_EVENT_NAMES.SUBSCRIBE,
  INSTALLED: CONVERSION_EVENT_NAMES.PURCHASE,
};
